// src/components/admin/AdminSidebar.jsx  
import React from "react";  
import { Link, useLocation } from "react-router-dom";  
import { motion, AnimatePresence } from "framer-motion";  
import { Users, ScrollText, LayoutDashboard, ChevronLeft, ChevronRight, LogOut } from "lucide-react";  
  
const menuItems = [  
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },  
  { to: "/admin/users", label: "Users", icon: Users },  
  { to: "/admin/trade-logs", label: "Trade logs", icon: ScrollText },  
];  
  
const AdminSidebar = ({ collapsed, isMobile, mobileMenuOpen, toggleSidebar, handleLogout }) => {  
  const location = useLocation();  
  
  const isActive = (path, exact) =>  
    exact ? location.pathname === path : location.pathname.startsWith(path);  
  
  const content = (  
    <>  
      {/* Brand */}  
      <div className="p-4 border-b border-gray-800">  
        <div className="flex items-center justify-between">  
          <Link to="/admin" className="flex items-center gap-2.5 min-w-0">  
            <div className="w-9 h-9 flex-shrink-0 bg-gradient-to-br from-gold to-yellow-600 rounded-lg flex items-center justify-center shadow-lg shadow-gold/20">  
              <svg className="w-4.5 h-4.5 text-black" fill="currentColor" viewBox="0 0 20 20">  
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />  
              </svg>  
            </div>  
            <AnimatePresence>  
              {!collapsed && (  
                <motion.div  
                  initial={{ opacity: 0, x: -6 }}  
                  animate={{ opacity: 1, x: 0 }}  
                  exit={{ opacity: 0, x: -6 }}  
                  className="min-w-0"  
                >  
                  <h1 className="text-sm font-bold text-white truncate">iTrader</h1>  
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Admin panel</p>  
                </motion.div>  
              )}  
            </AnimatePresence>  
          </Link>  
  
          {!isMobile && (  
            <button  
              onClick={toggleSidebar}  
              className="p-1.5 rounded-lg text-gray-500 hover:text-gold hover:bg-white/[0.03] transition-all"  
              title={collapsed ? "Expand" : "Collapse"}  
            >  
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}  
            </button>  
          )}  
        </div>  
      </div>  
  
      {/* Nav */}  
      <nav className="flex-1 overflow-y-auto py-4 px-2.5">  
        <AnimatePresence>  
          {!collapsed && (  
            <motion.p  
              initial={{ opacity: 0 }}  
              animate={{ opacity: 1 }}  
              exit={{ opacity: 0 }}  
              className="px-2 mb-3 text-[10px] font-semibold text-gray-600 uppercase tracking-wider"  
            >  
              Navigation  
            </motion.p>  
          )}  
        </AnimatePresence>  
  
        <div className="space-y-1">  
          {menuItems.map((item) => {  
            const active = isActive(item.to, item.exact);  
            const Icon = item.icon;  
            return (  
              <Link  
                key={item.to}  
                to={item.to}  
                title={collapsed ? item.label : undefined}  
                className={`group relative flex items-center gap-3 px-2.5 py-2.5 rounded-lg transition-all ${  
                  active  
                    ? "bg-gold/10 text-gold"  
                    : "text-gray-400 hover:text-white hover:bg-white/[0.03]"  
                }`}  
              >  
                {active && (  
                  <motion.span  
                    layoutId="adminActiveBar"  
                    className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-gold rounded-r-full"  
                    transition={{ type: "spring", bounce: 0.15, duration: 0.4 }}  
                  />  
                )}  
                <Icon size={18} className="flex-shrink-0" />  
                <AnimatePresence>  
                  {!collapsed && (  
                    <motion.span  
                      initial={{ opacity: 0, x: -6 }}  
                      animate={{ opacity: 1, x: 0 }}  
                      exit={{ opacity: 0, x: -6 }}  
                      className="text-sm font-medium"  
                    >  
                      {item.label}  
                    </motion.span>  
                  )}  
                </AnimatePresence>  
  
                {/* Collapsed tooltip */}  
                {collapsed && (  
                  <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-gray-900 border border-gray-800 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50 shadow-xl">  
                    <p className="text-xs font-medium text-white">{item.label}</p>  
                  </div>  
                )}  
              </Link>  
            );  
          })}  
        </div>  
      </nav>  
  
      {/* Logout */}  
      <div className="p-2.5 border-t border-gray-800">  
        <button  
          onClick={handleLogout}  
          title={collapsed ? "Sign out" : undefined}  
          className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/[0.06] transition-all group ${  
            collapsed ? "justify-center" : ""  
          }`}  
        >  
          <LogOut size={18} className="flex-shrink-0" />  
          <AnimatePresence>  
            {!collapsed && (  
              <motion.span  
                initial={{ opacity: 0, x: -6 }}  
                animate={{ opacity: 1, x: 0 }}  
                exit={{ opacity: 0, x: -6 }}  
                className="text-sm font-medium"  
              >  
                Sign out  
              </motion.span>  
            )}  
          </AnimatePresence>  
        </button>  
      </div>  
    </>  
  );  
  
  if (isMobile) {  
    return (  
      <AnimatePresence>  
        {mobileMenuOpen && (  
          <motion.aside  
            initial={{ x: -280 }}  
            animate={{ x: 0 }}  
            exit={{ x: -280 }}  
            transition={{ type: "spring", damping: 26, stiffness: 220 }}  
            className="fixed left-0 top-0 bottom-0 w-64 bg-[#0a0a0a] border-r border-gray-800 z-50 flex flex-col"  
          >  
            {content}  
          </motion.aside>  
        )}  
      </AnimatePresence>  
    );  
  }  
  
  return (  
    <aside  
      className={`fixed left-0 top-0 bottom-0 bg-[#0a0a0a] border-r border-gray-800 z-40 flex flex-col transition-all duration-300 ${  
        collapsed ? "w-16" : "w-64"  
      }`}  
    >  
      {content}  
    </aside>  
  );  
};  
  
export default AdminSidebar;  